// ═══════════════════════════════════════════════════════════════
// RollCall — Event Detail with check-in roster
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Stack, Badge, Button, Modal, Select,
  SimpleGrid, Center, Loader, NumberInput,
} from '../../components/gab/GabComponents';
import {
  IconArrowLeft, IconUserPlus, IconUsers, IconLock,
  IconClipboardCheck, IconLogin, IconLogout, IconBuilding,
  IconDownload, IconReceipt,
} from '@tabler/icons-react';
import { jsPDF } from 'jspdf';
import { notifications } from '../../lib/notifications';
import { useAuthStore } from '../../store/authStore';
import { useRollCallStore } from '../../store/rollcallStore';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { RC_EVENT_TYPE_META } from '../../types';
import type { RCEventType } from '../../types';
import { primary } from '../../lib/colors';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

const METHOD_COLORS: Record<string, string> = {
  pin: 'var(--color-primary-700)', badge: 'var(--color-success)', mobile: '#C68700', manual: '#939598',
};

export default function EventDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canEdit } = useModulePermission('rollcall');
  const { user, incidents, loadIncidents } = useAuthStore();
  const {
    events, personnel, checkins,
    loadEvents, loadPersonnel, loadCheckins,
    checkIn, checkOut, bulkCheckOut, closeEvent,
  } = useRollCallStore();

  const [loaded, setLoaded] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualPersonnelId, setManualPersonnelId] = useState<string | null>(null);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // Submit to PA Grants
  const [showPAModal, setShowPAModal] = useState(false);
  const [paProjectId, setPAProjectId] = useState<string | null>(null);
  const [perMealCost, setPerMealCost] = useState<number>(12);
  const [paCostType, setPACostType] = useState('materials');
  const [paSubmitting, setPASubmitting] = useState(false);
  const { projects, fetchProjects, createProjectCost, createDocument } = usePAGrantsStore();

  useEffect(() => {
    Promise.all([loadIncidents(), loadEvents(), loadPersonnel(), loadCheckins(), fetchProjects()])
      .then(() => setLoaded(true));
    const interval = setInterval(loadCheckins, 15000);
    return () => clearInterval(interval);
  }, []);

  const event = useMemo(() => events.find(e => String(e.id) === id), [events, id]);
  const incident = useMemo(
    () => event ? incidents.find(i => String(i.id) === String(event.incidentId)) : null,
    [event, incidents]
  );
  const eventCheckins = useMemo(
    () => checkins.filter(c => String(c.eventId) === id)
      .sort((a, b) => (b.checkInTime || '').localeCompare(a.checkInTime || '')),
    [checkins, id]
  );
  const currentlyIn = eventCheckins.filter(c => !c.checkOutTime);
  const checkedOut = eventCheckins.filter(c => !!c.checkOutTime);
  const uniqueAgencies = [...new Set(eventCheckins.map(c => c.agency).filter(Boolean))];

  const handleManualCheckIn = async () => {
    if (!manualPersonnelId || !id) return;
    const person = personnel.find(p => String(p.id) === manualPersonnelId);
    if (!person) return;
    try {
      await checkIn({
        eventId: id,
        personnelId: String(person.id),
        personnelName: `${person.firstName} ${person.lastName}`,
        agency: person.agency,
        position: person.position,
        method: 'manual',
      });
      notifications.show({ message: `${person.firstName} ${person.lastName} checked in`, color: 'var(--color-success)' });
      setShowManualModal(false);
      setManualPersonnelId(null);
    } catch (err: any) {
      notifications.show({ message: err?.message || 'Check-in failed', color: 'var(--color-error)' });
    }
  };

  const handleCheckOut = async (checkinId: number) => {
    try {
      await checkOut(checkinId, user?.fullName ?? 'system');
      notifications.show({ message: 'Checked out', color: 'var(--color-primary-700)' });
    } catch (err: any) {
      notifications.show({ message: err?.message || 'Check-out failed', color: 'var(--color-error)' });
    }
  };

  const handleBulkCheckOut = async () => {
    if (!id) return;
    try {
      const count = await bulkCheckOut(id, user?.fullName ?? 'system');
      notifications.show({ message: `${count} personnel checked out`, color: 'var(--color-primary-700)' });
      setShowBulkConfirm(false);
    } catch (err: any) {
      notifications.show({ message: err?.message || 'Bulk check-out failed', color: 'var(--color-error)' });
    }
  };

  const handleCloseEvent = async () => {
    if (!event) return;
    try {
      await closeEvent(event.id);
      notifications.show({ message: 'Event closed', color: '#939598' });
      setShowCloseConfirm(false);
    } catch (err: any) {
      notifications.show({ message: err?.message || 'Failed to close', color: 'var(--color-error)' });
    }
  };

  // PA project options for meal submission
  const paProjectOptions = useMemo(() =>
    projects
      .filter(p => p.workType !== 'hmgp')
      .map(p => ({ value: String(p.Rid), label: `${p.projectNumber} — ${p.projectTitle}` })),
    [projects]
  );

  if (!loaded && !event) {
    return <Center py="xl"><Loader /></Center>;
  }

  // ── Export functions ──────────────────────────────────────
  function exportCSV() {
    if (!event) return;
    const headers = ['Name', 'Agency', 'Position', 'Check-In', 'Check-Out', 'Method', 'Status'];
    const rows = eventCheckins.map(c => [
      c.personnelName, c.agency || '', c.position || '',
      c.checkInTime || '', c.checkOutTime || '', c.method || '',
      c.checkOutTime ? 'Checked Out' : 'Checked In',
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `rollcall-${(event.eventName || 'event').replace(/[^a-zA-Z0-9]/g, '_')}-${event.eventDate || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  function exportPDF() {
    if (!event) return;
    const doc = new jsPDF({ unit: 'in', format: 'letter' });
    const pw = 8.5;
    const m = 0.6;
    let y = 0.6;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text(`Roll Call: ${event.eventName || 'Event'}`, m, y); y += 0.25;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Date: ${event.eventDate || '—'} | Location: ${event.location || '—'} | Status: ${event.eventStatus}`, m, y); y += 0.2;
    doc.text(`Total: ${eventCheckins.length} | Currently In: ${currentlyIn.length} | Checked Out: ${checkedOut.length} | Agencies: ${uniqueAgencies.length}`, m, y); y += 0.35;
    // Table header
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Name', m, y); doc.text('Agency', m + 2, y); doc.text('Position', m + 3.5, y);
    doc.text('In', m + 5, y); doc.text('Out', m + 6, y); doc.text('Status', m + 7, y);
    y += 0.05;
    doc.setDrawColor(200); doc.line(m, y, pw - m, y); y += 0.15;
    doc.setFont('helvetica', 'normal');
    for (const c of eventCheckins) {
      if (y > 10) { doc.addPage(); y = 0.6; }
      doc.text((c.personnelName || '').substring(0, 25), m, y);
      doc.text((c.agency || '').substring(0, 18), m + 2, y);
      doc.text((c.position || '').substring(0, 18), m + 3.5, y);
      doc.text(c.checkInTime ? new Date(c.checkInTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '', m + 5, y);
      doc.text(c.checkOutTime ? new Date(c.checkOutTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '', m + 6, y);
      doc.text(c.checkOutTime ? 'Out' : 'In', m + 7, y);
      y += 0.18;
    }
    doc.save(`rollcall-${(event.eventName || 'event').replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
  }

  async function handleSubmitToPA() {
    if (!paProjectId || !event) return;
    setPASubmitting(true);
    try {
      const mealCount = eventCheckins.length;
      const totalCost = mealCount * perMealCost;
      const mealLabel = event.mealType
        ? (event.mealType.charAt(0) || '').toUpperCase() + event.mealType.slice(1)
        : 'Meal';
      const today = new Date().toISOString().split('T')[0];

      // 1. Create cost line item
      await createProjectCost({
        projectId: paProjectId,
        costType: paCostType,
        cefPart: paCostType === 'contracts' ? 'E' : 'C',
        description: `${mealLabel} service — ${event.eventName} (${event.eventDate || ''}) — ${mealCount} meals @ $${perMealCost.toFixed(2)}/meal`,
        quantity: String(mealCount),
        unit: 'EA',
        unitCost: String(perMealCost),
        totalCost: String(totalCost),
        sourceReference: `RollCall Event #${event.id}`,
        estimatedOrActual: 'actual',
        eligible: 'true',
        notes: `Auto-generated from RollCall meal event. ${uniqueAgencies.length} agencies, ${currentlyIn.length} currently in, ${checkedOut.length} checked out.`,
      });

      // 2. Generate meal roster summary and attach as document
      const rosterLines = eventCheckins.map(c =>
        `${c.personnelName || '?'} | ${c.agency || '—'} | ${c.position || '—'} | In: ${c.checkInTime ? new Date(c.checkInTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '?'} | Out: ${c.checkOutTime ? new Date(c.checkOutTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}`
      ).join('\n');
      const summaryText = [
        `MEAL ROSTER — ${event.eventName}`,
        `Date: ${event.eventDate || '—'}`,
        `Type: ${mealLabel}`,
        `Location: ${event.location || '—'}`,
        `Total Meals Served: ${mealCount}`,
        `Agencies: ${uniqueAgencies.join(', ') || '—'}`,
        `Cost: ${mealCount} × $${perMealCost.toFixed(2)} = $${totalCost.toFixed(2)}`,
        ``,
        `NAME | AGENCY | POSITION | CHECK-IN | CHECK-OUT`,
        `${'—'.repeat(60)}`,
        rosterLines,
        ``,
        `Generated from RollCall Event #${event.id} on ${today}`,
      ].join('\n');

      await createDocument({
        projectId: paProjectId,
        documentName: `Meal Roster — ${mealLabel} ${event.eventDate || ''}.txt`,
        documentType: 'Force Account Labor Records',
        description: `${mealCount} meals served at ${event.eventName}. Auto-generated from RollCall.`,
        uploadDate: today,
        uploadedBy: user?.email || '',
        tags: 'force_account_labor',
        notes: summaryText,
      } as any);

      notifications.show({ title: 'Submitted', message: `${mealCount} meals ($${totalCost.toLocaleString()}) + meal roster added to PA project.`, color: 'var(--color-success)' });
      setShowPAModal(false);
    } catch (err) {
      notifications.show({ title: 'Error', message: err instanceof Error ? err.message : 'Failed to submit', color: 'var(--color-error)' });
    } finally {
      setPASubmitting(false);
    }
  }

  if (!event) {
    return (
      <Center py="xl">
        <Stack align="center">
          <Title order={3}>Event not found</Title>
          <Button leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate('/rollcall/events')}>Back to Events</Button>
        </Stack>
      </Center>
    );
  }

  const meta = RC_EVENT_TYPE_META[event.eventType as RCEventType] || RC_EVENT_TYPE_META.custom;

  return (
    <div>
      <Breadcrumbs items={[
        { label: 'Hub', to: '/' },
        { label: 'RollCall', to: '/rollcall' },
        { label: event?.name || 'Event' },
      ]} />
      <Button variant="subtle" size="compact-sm" leftSection={<IconArrowLeft size={14} />}
        onClick={() => navigate('/rollcall/events')} mb="sm">Back to Events</Button>

      <Group justify="space-between" mb="md">
        <div>
          <Group gap="xs" mb={4}>
            <Title order={3} c="black">{event.eventName}</Title>
            <Badge color={meta.color}>{meta.label}</Badge>
            <Badge color={event.eventStatus === 'open' ? 'var(--color-success)' : '#939598'}>{event.eventStatus}</Badge>
          </Group>
          <Text size="sm" c="dimmed">
            {incident?.name || '—'} · {event.eventDate || '—'}
            {event.location ? ` · ${event.location}` : ''}
          </Text>
        </div>
      </Group>

      {/* Stats */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md">
        <Paper p="sm" radius="sm" withBorder>
          <Group gap="xs" mb={2}>
            <IconClipboardCheck size={14} style={{ color: primary[700] }} />
            <Text size="xs" c="dimmed">Total</Text>
          </Group>
          <Text fz={24} fw={700}>{eventCheckins.length}</Text>
        </Paper>
        <Paper p="sm" radius="sm" withBorder>
          <Group gap="xs" mb={2}>
            <IconLogin size={14} style={{ color: '#07963F' }} />
            <Text size="xs" c="dimmed">Currently In</Text>
          </Group>
          <Text fz={24} fw={700}>{currentlyIn.length}</Text>
        </Paper>
        <Paper p="sm" radius="sm" withBorder>
          <Group gap="xs" mb={2}>
            <IconLogout size={14} style={{ color: primary[900] }} />
            <Text size="xs" c="dimmed">Checked Out</Text>
          </Group>
          <Text fz={24} fw={700}>{checkedOut.length}</Text>
        </Paper>
        <Paper p="sm" radius="sm" withBorder>
          <Group gap="xs" mb={2}>
            <IconBuilding size={14} style={{ color: '#A67103' }} />
            <Text size="xs" c="dimmed">Agencies</Text>
          </Group>
          <Text fz={24} fw={700}>{uniqueAgencies.length}</Text>
        </Paper>
      </SimpleGrid>

      {/* Actions */}
      {canEdit && event.eventStatus === 'open' && (
        <Group mb="md" gap="xs">
          <Button size="xs" leftSection={<IconUserPlus size={14} />}
            onClick={() => setShowManualModal(true)}>Manual Check-In</Button>
          <Button size="xs" variant="light" color="#C68700" leftSection={<IconUsers size={14} />}
            onClick={() => setShowBulkConfirm(true)} disabled={currentlyIn.length === 0}>
            Bulk Check-Out ({currentlyIn.length})</Button>
          <Button size="xs" variant="light" color="var(--color-error)" leftSection={<IconLock size={14} />}
            onClick={() => setShowCloseConfirm(true)}>Close Event</Button>
        </Group>
      )}

      {/* Export + PA Submit */}
      {eventCheckins.length > 0 && (
        <Group mb="md" gap="xs">
          <Button size="xs" variant="light" leftSection={<IconDownload size={14} />} onClick={exportCSV}>
            Export CSV
          </Button>
          <Button size="xs" variant="light" color="var(--color-error)" leftSection={<IconDownload size={14} />} onClick={exportPDF}>
            Export PDF
          </Button>
          {event?.eventType === 'meal' && canEdit && (
            <Button size="xs" variant="light" color="var(--color-success)" leftSection={<IconReceipt size={14} />}
              onClick={() => setShowPAModal(true)}>
              Submit Meals to PA
            </Button>
          )}
        </Group>
      )}

      {/* Check-ins Table */}
      <Paper p="md" radius="sm" withBorder>
        <Text fw={600} size="sm" mb="sm">Check-ins</Text>
        {eventCheckins.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">
            {event.eventStatus === 'open'
              ? 'No check-ins yet. Use manual check-in or mobile check-in.'
              : 'No recorded check-ins.'}
          </Text>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--frost)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 600 }}>Name</th>
                  <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 600 }}>Agency</th>
                  <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 600 }}>Position</th>
                  <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 600 }}>In</th>
                  <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 600 }}>Out</th>
                  <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 600 }}>Method</th>
                  <th style={{ padding: '8px 16px' }}></th>
                </tr>
              </thead>
              <tbody>
                {eventCheckins.map(c => {
                  const inTime = c.checkInTime ? new Date(c.checkInTime).toLocaleTimeString() : '—';
                  const outTime = c.checkOutTime ? new Date(c.checkOutTime).toLocaleTimeString() : null;
                  return (
                    <tr key={c.id} style={{ borderBottom: '1px solid var(--frost)' }}>
                      <td style={{ padding: '8px 16px', fontWeight: 600 }}>{c.personnelName}</td>
                      <td style={{ padding: '8px 16px' }}>{c.agency || '—'}</td>
                      <td style={{ padding: '8px 16px', textTransform: 'capitalize' }}>
                        {(c.position || '—').replace(/_/g, ' ')}
                      </td>
                      <td style={{ padding: '8px 16px', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{inTime}</td>
                      <td style={{ padding: '8px 16px', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                        {outTime || <Badge color="var(--color-success)" size="xs">IN</Badge>}
                      </td>
                      <td style={{ padding: '8px 16px' }}>
                        <Badge color={METHOD_COLORS[c.method] || '#939598'} size="xs">{c.method}</Badge>
                      </td>
                      <td style={{ padding: '8px 16px' }}>
                        {canEdit && !c.checkOutTime && event.eventStatus === 'open' && (
                          <Button size="compact-xs" variant="subtle" color="var(--color-error)"
                            onClick={() => handleCheckOut(c.id)}>Check Out</Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Paper>

      {/* Manual Check-In Modal */}
      <Modal opened={showManualModal} onClose={() => setShowManualModal(false)}
        title="Manual Check-In" centered>
        <Select label="Personnel" placeholder="Select person" searchable
          data={personnel.filter(p => p.status === 'active')
            .map(p => ({ value: String(p.id), label: `${p.firstName} ${p.lastName} — ${p.agency || 'No Agency'}` }))}
          value={manualPersonnelId} onChange={setManualPersonnelId} mb="md" />
        <Group justify="flex-end">
          <Button variant="light" onClick={() => setShowManualModal(false)}>Cancel</Button>
          <Button onClick={handleManualCheckIn} disabled={!manualPersonnelId}
            leftSection={<IconLogin size={14} />}>Check In</Button>
        </Group>
      </Modal>

      {/* Bulk Check-Out Confirm */}
      <Modal opened={showBulkConfirm} onClose={() => setShowBulkConfirm(false)}
        title="Bulk Check-Out" centered size="sm">
        <Text size="sm" mb="md">Check out all {currentlyIn.length} currently checked-in personnel?</Text>
        <Group justify="flex-end">
          <Button variant="light" onClick={() => setShowBulkConfirm(false)}>Cancel</Button>
          <Button color="#C68700" onClick={handleBulkCheckOut}>Check Out All</Button>
        </Group>
      </Modal>

      {/* Close Event Confirm */}
      <Modal opened={showCloseConfirm} onClose={() => setShowCloseConfirm(false)}
        title="Close Event" centered size="sm">
        <Text size="sm" mb="md">Close this event? No more check-ins will be accepted.</Text>
        <Group justify="flex-end">
          <Button variant="light" onClick={() => setShowCloseConfirm(false)}>Cancel</Button>
          <Button color="var(--color-error)" onClick={handleCloseEvent}>Close Event</Button>
        </Group>
      </Modal>

      {/* Submit Meals to PA Modal */}
      <Modal opened={showPAModal} onClose={() => setShowPAModal(false)}
        title="Submit Meal Costs to PA Project" size="md">
        <Stack gap="sm">
          <Paper p="sm" bg="green.0" radius="sm" style={{ border: '1px solid rgba(3,119,48,0.14)' }}>
            <Group justify="space-between">
              <Text size="sm" fw={500}>Meals served</Text>
              <Text size="lg" fw={700} c="var(--color-success)">{eventCheckins.length}</Text>
            </Group>
            <Text size="xs" c="dimmed">
              {event?.name} — {event?.eventDate || ''}
              {event?.mealType ? ` (${event.mealType})` : ''}
            </Text>
          </Paper>

          <Select
            label="PA Project"
            data={paProjectOptions}
            value={paProjectId}
            onChange={setPAProjectId}
            placeholder="Select project for reimbursement"
            searchable
            required
          />

          <NumberInput
            label="Cost per Meal"
            description="FEMA typically allows $10-$15/meal for mass feeding"
            value={perMealCost}
            onChange={v => setPerMealCost(Number(v) || 0)}
            min={0}
            decimalScale={2}
            prefix="$"
          />

          <Select
            label="Cost Type"
            data={[
              { value: 'materials', label: 'Materials (CEF C) — purchased food/supplies' },
              { value: 'contracts', label: 'Contracts (CEF E) — catered by vendor' },
            ]}
            value={paCostType}
            onChange={v => setPACostType(v || 'materials')}
          />

          <Paper p="sm" bg="gray.0" radius="sm">
            <Group justify="space-between">
              <Text size="sm" fw={500}>Total Cost</Text>
              <Text size="lg" fw={700}>
                ${(eventCheckins.length * perMealCost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Text>
            </Group>
            <Text size="xs" c="dimmed">
              {eventCheckins.length} meals × ${perMealCost.toFixed(2)}/meal
            </Text>
          </Paper>

          <Group justify="flex-end" mt="xs">
            <Button variant="subtle" onClick={() => setShowPAModal(false)}>Cancel</Button>
            <Button color="var(--color-success)" leftSection={<IconReceipt size={14} />}
              onClick={handleSubmitToPA} loading={paSubmitting}
              disabled={!paProjectId}>
              Submit to PA Project
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
